import { BlueprintOperationType } from "../../../models/Blueprints";
import { ScraperVersion } from "../../scraper/types";
import { EditableText } from "../../shared/EditableText";
import useBlueprintContext from "../context/useBlueprintContext";
import BlueprintEditorRightPanelExistingStepForm from "./BlueprintEditorRightPanelExistingStepForm";
import BlueprintEditorRightPanelNewStepForm from "./BlueprintEditorRightPanelNewStepForm";
import BlueprintEditorRightPanelScraperForm from "./BlueprintEditorRightPanelScraperForm";
import BlueprintEditorRightPanelTriggerForm from "./BlueprintEditorRightPanelTriggerForm";
import { showErrorToast } from "../../shared/Toasts";
import { Divider, Text } from "@merge-api/merge-javascript-shared";

const BlueprintEditorRightPanel = ({ width = 350 }: { width?: number }) => {
  const { renameStep, selectedStep, blueprint } = useBlueprintContext();

  if (!selectedStep) {
    return <></>;
  }
  const stepID = selectedStep?.id ?? "";
  const stepTemplate = selectedStep?.template;

  const getConfig = () => {
    if (stepTemplate === undefined) {
      return {
        title: "Trigger",
        subtitle: "Control when this Blueprint executes",
      };
    } else if (stepTemplate === "ghost") {
      return {
        title: "New step",
        subtitle: undefined,
      };
    } else {
      return {
        title: stepTemplate.name,
        subtitle: stepID,
      };
    }
  };

  const { title, subtitle } = getConfig();

  const onSaveStepID = (newName: string, onError?: () => void) => {
    if (newName) {
      renameStep(stepID, newName, onError);
    } else {
      showErrorToast("Step name cannot be empty.");
    }
  };

  const showTriggerForm = stepTemplate === undefined && !(selectedStep && "steps" in selectedStep);

  const isReportBlueprint = !!blueprint.report_template_id;
  const isWebhookBlueprint = !!blueprint.webhook_receiver_event_type_id;
  const isScheduledBlueprint = [
    BlueprintOperationType.FETCH,
    BlueprintOperationType.WEBHOOK_SETUP,
  ].includes(blueprint.operation_type);

  // Webhook/Report blueprints don't run at scheduled intervals, but rather whenever we receive a webhook/report file
  const blueprintRunsOnScheduledIntervals =
    !(isReportBlueprint || isWebhookBlueprint) && isScheduledBlueprint;

  if (showTriggerForm && !blueprintRunsOnScheduledIntervals) {
    return <></>;
  }

  return (
    <div
      style={{ width: `${width}px`, minWidth: `${width}px` }}
      className="flex flex-col h-full bg-white border-l border-[#dce4f0] overflow-y-auto overflow-x-hidden scrollbar-hide"
    >
      <div className="flex flex-col text-center p-4">
        <Text variant="h4" className="mb-2 break-words">
          {title}
        </Text>
        {subtitle && stepTemplate && stepTemplate !== "ghost" && (
          <EditableText originalText={stepID} onSave={onSaveStepID} hasEditShortcut={true} />
        )}
      </div>

      <Divider />
      <div className="flex flex-col flex-grow min-h-0">
        {stepTemplate === undefined ? (
          selectedStep && "steps" in selectedStep ? (
            <BlueprintEditorRightPanelScraperForm scraper={selectedStep as ScraperVersion} />
          ) : (
            <BlueprintEditorRightPanelTriggerForm />
          )
        ) : stepTemplate === "ghost" ? (
          <BlueprintEditorRightPanelNewStepForm />
        ) : (
          <BlueprintEditorRightPanelExistingStepForm
            key={stepID}
            stepID={stepID}
            stepTemplate={stepTemplate}
          />
        )}
      </div>
    </div>
  );
};

export default BlueprintEditorRightPanel;
